// Comunicado da marca (#6117) — ex.: o do Guilherme, da Gran, sobre o atendimento passar a
// ser da Vigor. Informativo: sem "li e aceito", só "Entendi". Usado pelo Gate (abre sozinho
// no primeiro acesso) e pelo cartão do Início (reler).
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Mail, MessageCircle, X } from 'lucide-react';
import { useMarcaPortal } from './MarcaPortalContext';
import LogoMarcaPortal from './LogoMarcaPortal';
import { linkWhatsapp } from './marcaPortal';
import { formatWhatsapp } from '../format';

export interface ComunicadoMarca {
  id: string;
  titulo: string;
  mensagem: string;
  url_anexo?: string | null;
}

interface Props {
  comunicado: ComunicadoMarca;
  onFechar: () => void;
}

const ComunicadoMarcaModal: React.FC<Props> = ({ comunicado, onFechar }) => {
  const { marca } = useMarcaPortal();
  const botaoRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    botaoRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = overflow; };
  }, [onFechar]);

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="comunicado-marca-titulo">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onFechar} />
      {/* Largo no computador: a imagem do comunicado é o conteúdo principal e ficava ilegível
          num modal estreito. Em tela grande, imagem à esquerda e texto à direita. */}
      <div className="relative w-full sm:max-w-3xl lg:max-w-6xl max-h-[94vh] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden">
        <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3">
          <LogoMarcaPortal variante="cabecalho" semSelo />
          <button onClick={onFechar} className="p-2 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100" aria-label="Fechar comunicado">
            <X size={18} />
          </button>
        </div>
        <div className={`flex-1 min-h-0 overflow-y-auto px-5 pb-4 ${comunicado.url_anexo ? 'lg:grid lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:gap-6 lg:items-start' : ''}`}>
          {comunicado.url_anexo && (
            <img src={comunicado.url_anexo} alt="Imagem do comunicado" className="w-full rounded-2xl border border-gray-100 mb-4 lg:mb-0 lg:order-1" />
          )}
          <div className="space-y-4 lg:order-2">
            <h2 id="comunicado-marca-titulo" className="text-lg sm:text-xl font-bold text-gray-900 leading-snug">{comunicado.titulo}</h2>
            <p className="text-sm sm:text-[15px] text-gray-700 leading-relaxed whitespace-pre-line">{comunicado.mensagem}</p>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-gray-100 space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <a
              href={linkWhatsapp(marca)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-primary border border-primary/20 hover:bg-primary/5"
            >
              <MessageCircle size={16} aria-hidden="true" /> {formatWhatsapp(marca.contato.whatsapp)}
            </a>
            <a
              href={`mailto:${marca.contato.email}`}
              className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-primary border border-primary/20 hover:bg-primary/5"
            >
              <Mail size={16} aria-hidden="true" /> E-mail
            </a>
          </div>
          <button
            ref={botaoRef}
            onClick={onFechar}
            className="w-full py-3 rounded-xl text-sm font-bold text-white bg-primary hover:bg-primary/90"
          >
            Entendi
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ComunicadoMarcaModal;
